// inline-predictive-search.js
import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Inline predictive search component that shows results above the form
 * @extends {Component}
 */
class InlinePredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'resultsWrapper'];

  #controller = new AbortController();
  #activeFetch = null;
  #emptyStateLoaded = false;
  #closeTimer = null;
  #isInputFocused = false;
  #isResultsHovered = false;

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    // Event listeners
    this.refs.searchInput.addEventListener('input', this.search, { signal });
    this.refs.searchInput.addEventListener('keydown', this.onSearchKeyDown, { signal });
    this.refs.searchInput.addEventListener('focus', this.#handleInputFocus, { signal });
    this.refs.searchInput.addEventListener('blur', this.#handleInputBlur, { signal });
    
    // IMPORTANT: Use proper binding for reset button
    this.refs.resetButton.addEventListener('click', (event) => {
        this.resetSearch(event);
    }, { signal });
    
    // Mouse events for results hover
    this.refs.resultsWrapper.addEventListener('mouseenter', this.#handleResultsMouseEnter, { signal });
    this.refs.resultsWrapper.addEventListener('mouseleave', this.#handleResultsMouseLeave, { signal });

    // Click outside listener
    document.addEventListener('click', this.#handleClickOutside, { signal });

    // Window resize listener for repositioning
    window.addEventListener('resize', this.#handleWindowResize, { signal });

    if (this.refs.searchInput.value.length > 0) {
        this.#showResetButton();
    }

    // Load recently viewed products on idle
    if (RecentlyViewed.getProducts().length > 0) {
        requestIdleCallback(() => {
        this.#loadEmptyState();
        });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
    }
  }

  #handleInputFocus = () => {
    this.#isInputFocused = true;
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
    }
    
    // Show results if we have content
    const hasResults = this.refs.predictiveSearchResults.children.length > 0;
    if (hasResults) {
      this.#showResults();
    }
  };

  #handleInputBlur = () => {
    this.#isInputFocused = false;
    
    // If results are being hovered, don't close immediately
    if (!this.#isResultsHovered) {
      this.#scheduleCloseResults();
    }
  };

  #handleResultsMouseEnter = () => {
    this.#isResultsHovered = true;
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
    }
  };

  #handleResultsMouseLeave = () => {
    this.#isResultsHovered = false;
    // If input is not focused, close results after a short delay
    if (!this.#isInputFocused) {
      this.#scheduleCloseResults();
    }
  };

  #scheduleCloseResults = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
    }
    
    this.#closeTimer = setTimeout(() => {
      this.#closeResults();
    }, 200); // Short delay to allow for clicking on results
  };

  #handleClickOutside = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    
    // If click is outside the component
    if (!this.contains(target)) {
      this.#closeResults();
      this.refs.searchInput.blur();
    }
  };

  #handleWindowResize = debounce(() => {
    if (this.refs.resultsWrapper.classList.contains('is-visible')) {
      this.#positionResults();
    }
  }, 100);

  #showResults = () => {
    const { resultsWrapper } = this.refs;
    
    // Remove hidden attribute and add visible class
    resultsWrapper.removeAttribute('hidden');
    resultsWrapper.classList.remove('is-hidden');
    resultsWrapper.classList.add('is-visible');
    
    // Position the results (above by default)
    this.#positionResults();
    
    // Set aria-expanded attribute
    resultsWrapper.setAttribute('aria-expanded', 'true');
  };

  #closeResults = () => {
    const { resultsWrapper, searchInput } = this.refs;
    
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    
    // If results are visible, hide them
    if (resultsWrapper.classList.contains('is-visible')) {
      resultsWrapper.classList.remove('is-visible');
      resultsWrapper.classList.add('is-hidden');
      resultsWrapper.setAttribute('aria-expanded', 'false');
      
      // Wait for transition to complete before hiding
      onAnimationEnd(resultsWrapper, () => {
      if (resultsWrapper.classList.contains('is-hidden')) {
          resultsWrapper.setAttribute('hidden', '');
          // Remove positioning classes
          resultsWrapper.classList.remove('position-above', 'position-below', 'mobile-position');
      }
      }, { once: true });
    }
  };

  #positionResults = () => {
    const { resultsWrapper, form } = this.refs;
    
    if (!resultsWrapper || !form) return;
    
    // Remove previous positioning classes
    resultsWrapper.classList.remove('position-above', 'position-below');
    
    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const formRect = form.getBoundingClientRect();
    const wrapperHeight = resultsWrapper.offsetHeight || 400; // Default max-height
    
    // Calculate available space
    const spaceAbove = formRect.top;
    const spaceBelow = viewportHeight - formRect.bottom;
    
    // Determine best position based on available space
    let positionClass = 'position-above'; // Default
    
    // Check if we should position below
    if (spaceAbove < wrapperHeight && spaceBelow >= spaceAbove) {
      positionClass = 'position-below';
    }
    
    // Add positioning class
    resultsWrapper.classList.add(positionClass);
    
    // Handle mobile positioning differently
    if (viewportWidth <= 749) {
      resultsWrapper.classList.add('mobile-position');
    } else {
      resultsWrapper.classList.remove('mobile-position');
    }
  };

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.scrollTo(0, 0);
    });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  resetSearch = (event) => {
    if (event) {
        event.preventDefault();
        event.stopPropagation(); // Prevent event bubbling
    }
    
    const { searchInput, predictiveSearchResults } = this.refs;
    
    // Clear the input value
    searchInput.value = '';
    
    // Hide reset button
    this.#hideResetButton();
    
    // Close results
    this.#closeResults();
    
    // Clear any search results
    predictiveSearchResults.innerHTML = '';
    
    // Remove focus from input
    searchInput.blur();
    
    // Reset keyboard navigation state
    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;
    
    // Reset to empty state if needed
    if (RecentlyViewed.getProducts().length > 0 && !this.#emptyStateLoaded) {
        this.#loadEmptyState();
    } else {
        // Force empty state reload if already loaded
        this.#emptyStateLoaded = false;
        this.#loadEmptyState();
    }
  };

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#closeResults();
      this.#hideResetButton();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults, resultsWrapper } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      
      if (abortController.signal.aborted || !resultsMarkup) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();
      
      // Remove hidden attribute before showing
      resultsWrapper.removeAttribute('hidden');
      
      // Force a reflow to ensure transition works properly
      resultsWrapper.getBoundingClientRect();
      
      this.#showResults();
    } catch (error) {
      if (!abortController.signal.aborted) {
        throw error;
      }
    }
  }

  onSearchKeyDown = (event) => {
    // Escape key closes results
    if (event.key === 'Escape') {
      if (this.refs.resultsWrapper.classList.contains('is-visible')) {
        this.#closeResults();
        return;
      }
    }

    // Arrow key navigation
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    // Find recently viewed elements in the current component
    const recentlyViewedWrapper = this.querySelector('[ref="recentlyViewedWrapper"]');
    const recentlyViewedTitle = this.querySelectorAll('[ref="recentlyViewedTitle[]"]');
    const recentlyViewedItems = this.querySelectorAll('[ref="recentlyViewedItems[]"]');

    const allRecentlyViewedElements = [...recentlyViewedItems, ...recentlyViewedTitle];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  async #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;

    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) return;

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        
        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
          if (collectionElement) {
            collectionElement.prepend(...recentlyViewedProductsHtml.children);
          }
        }
      }
    }

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }
}

if (!customElements.get('inline-predictive-search-component')) {
  customElements.define('inline-predictive-search-component', InlinePredictiveSearchComponent);
}